import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getExtolePersonByEmail, getExtolePersonById, getExtoleRewards } from '../services/api'

const Container = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Card = styled.div`
  background: ${theme.color.background.frame};
  border: 2px solid ${theme.color.accent.primary};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 8px ${theme.color.shadow.light};
  margin-bottom: 2rem;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`

const CardTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.accent.primary};
  margin: 0 0 2rem 0;
  text-align: center;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.color.border.light};
  border-top: 4px solid ${theme.color.accent.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 3rem auto;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  margin-top: 1rem;
`

const RewardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const RewardItem = styled.div`
  background: ${theme.color.background.light};
  border: 1px solid ${theme.color.border.light};
  border-radius: 8px;
  padding: 1.5rem;
`

const RewardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const RewardTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  text-transform: capitalize;
`

const RewardDate = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.grey};
`

const RewardDetails = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  margin-top: 0.5rem;
  padding: 1rem;
  background: ${theme.color.background.light};
  border-radius: 4px;
`

const ReferrerSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.color.border.light};
`

const ReferrerLabel = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.grey};
  font-weight: 600;
`

const ReferrerEmail = styled.div`
  font-size: 1.4rem;
  color: ${theme.color.text.base};
  flex: 1;
`

const SearchButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #b8002d;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
  font-style: italic;
`

interface Reward {
  id: string
  reward_supplier_id?: string
  face_value?: number
  face_value_type?: string
  earned_date?: string
  journey_name?: string
  data?: {
    other_person_id?: {
      name: string
      value: string
    }
  }
  referrerEmail?: string
}

interface FWBProps {
  userData: any
  onSearchEmail?: (email: string) => void
}

const FWB: React.FC<FWBProps> = ({ userData, onSearchEmail }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rewards, setRewards] = useState<Reward[]>([])
  const [extolePersonId, setExtolePersonId] = useState<string | null>(null)

  const userEmail = userData?.user?.email

  useEffect(() => {
    const fetchExtoleData = async () => {
      if (!userEmail) {
        setError('No email address found for this user')
        setLoading(false)
        return
      }

      // Demo mode: For j.kliman@barrys.com, show demo FWB rewards
      if (userEmail === 'j.kliman@barrys.com') {
        console.log('🎬 FWB Demo Mode: Generating demo rewards for j.kliman@barrys.com')
        const demoRewards: Reward[] = [
          {
            id: 'demo-reward-1',
            reward_supplier_id: 'demo-supplier',
            face_value: 1,
            face_value_type: 'class',
            earned_date: '2025-01-15T10:30:00Z',
            journey_name: 'advocate_reward',
            referrerEmail: 'sarah.jones@gmail.com'
          },
          {
            id: 'demo-reward-2',
            reward_supplier_id: 'demo-supplier',
            face_value: 1,
            face_value_type: 'class',
            earned_date: '2024-12-20T14:15:00Z',
            journey_name: 'advocate_reward',
            referrerEmail: 'mike.smith@outlook.com'
          },
          {
            id: 'demo-reward-3',
            reward_supplier_id: 'demo-supplier',
            face_value: 1,
            face_value_type: 'class',
            earned_date: '2024-11-05T09:00:00Z',
            journey_name: 'friend_reward',
            referrerEmail: 'alex.chen@company.com'
          }
        ]
        setRewards(demoRewards)
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Step 1: Get Extole person by email
        const personData = await getExtolePersonByEmail(userEmail)

        if (!personData || personData.length === 0) {
          setError('No Extole account found for this user')
          setLoading(false)
          return
        }

        const person = personData[0]
        const personId = person.id
        setExtolePersonId(personId)

        // Step 2: Get rewards for this person
        const rewardsData = await getExtoleRewards(personId)

        if (!rewardsData || rewardsData.length === 0) {
          setRewards([])
          setLoading(false)
          return
        }

        // Step 3: For each reward with other_person_id in data, fetch the referrer's email
        const rewardsWithReferrers = await Promise.all(
          rewardsData.map(async (reward: Reward) => {
            const otherPersonId = reward.data?.other_person_id?.value
            if (otherPersonId) {
              try {
                const referrerData = await getExtolePersonById(otherPersonId)
                return {
                  ...reward,
                  referrerEmail: referrerData.identity_key_value || null
                }
              } catch (err) {
                console.error(`Failed to fetch referrer for reward ${reward.id}:`, err)
                return reward
              }
            }
            return reward
          })
        )

        setRewards(rewardsWithReferrers)
      } catch (err: any) {
        console.error('Error fetching Extole data:', err)

        // Try to get more detailed error information
        const errorMessage = err.details || err.message || 'Failed to load Friend\'s With Benefits data'
        console.error('Detailed error:', errorMessage)

        setError(`${errorMessage}${err.status ? ` (Status: ${err.status})` : ''}`)
      } finally {
        setLoading(false)
      }
    }

    fetchExtoleData()
  }, [userEmail])

  const handleSearchReferrer = (email: string) => {
    if (onSearchEmail) {
      onSearchEmail(email)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getRewardType = (journeyName: string) => {
    if (journeyName === 'advocate_reward') {
      return 'Advocate Award'
    } else if (journeyName === 'friend_reward') {
      return 'Friend Award'
    }
    return journeyName.replace(/_/g, ' ')
  }

  const getRewardBenefits = (journeyName: string) => {
    if (journeyName === 'advocate_reward') {
      return 'Earned Rewards: 1x Complimentary Free Class, 200 Stars'
    } else if (journeyName === 'friend_reward') {
      return 'Earned Rewards: 1x Complimentary Free Class'
    }
    return 'Reward earned'
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <CardTitle>Friend's With Benefits</CardTitle>
          <LoadingSpinner />
        </Card>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Card>
          <CardTitle>Friend's With Benefits</CardTitle>
          <ErrorMessage>{error}</ErrorMessage>
        </Card>
      </Container>
    )
  }

  return (
    <Container>
      <Card>
        <CardTitle>Friend's With Benefits</CardTitle>
        {rewards.length === 0 ? (
          <NoDataMessage>No rewards found for this user</NoDataMessage>
        ) : (
          <RewardsList>
            {rewards.map((reward) => (
              <RewardItem key={reward.id}>
                <RewardHeader>
                  <RewardTitle>
                    {reward.journey_name ? getRewardType(reward.journey_name) : 'Reward'}
                  </RewardTitle>
                  {reward.earned_date && (
                    <RewardDate>{formatDate(reward.earned_date)}</RewardDate>
                  )}
                </RewardHeader>
                {reward.journey_name && (
                  <RewardDetails>{getRewardBenefits(reward.journey_name)}</RewardDetails>
                )}
                {reward.referrerEmail && (
                  <ReferrerSection>
                    <ReferrerLabel>
                      {reward.journey_name === 'advocate_reward' ? 'Referred:' : 'Referred by:'}
                    </ReferrerLabel>
                    <ReferrerEmail>{reward.referrerEmail}</ReferrerEmail>
                    <SearchButton onClick={() => handleSearchReferrer(reward.referrerEmail!)}>
                      Search User
                    </SearchButton>
                  </ReferrerSection>
                )}
              </RewardItem>
            ))}
          </RewardsList>
        )}
      </Card>
    </Container>
  )
}

export default FWB
